import { useState } from 'react';
import { Share2, Check, Car, Award, Calendar, Percent, X } from 'lucide-react';

export interface CertificateData {
  investorName: string;
  carMake: string;
  carModel: string;
  carYear: number;
  ownershipPct: number;
  investmentAmount: number;
  date: string; // ISO date string
}

interface OwnershipCertificateProps {
  data: CertificateData;
  onClose: () => void;
}

export function OwnershipCertificate({ data, onClose }: OwnershipCertificateProps) {
  const [copied, setCopied] = useState(false);

  const shareText = `I just became a fractional owner of a ${data.carYear} ${data.carMake} ${data.carModel} through FAM! Holding ${data.ownershipPct}% equity. Join the FAM network at fam.autos`;

  const handleShare = async () => {
    if (typeof navigator.share !== 'undefined') {
      try {
        await navigator.share({
          title: 'FAM Ownership Certificate',
          text: shareText,
          url: 'https://fam.autos',
        });
      } catch {
        // user cancelled — no-op
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const formattedDate = new Date(data.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative max-w-lg w-full">
        {/* Certificate card */}
        <div
          className="relative overflow-hidden rounded-2xl shadow-2xl"
          style={{ background: '#0f172a' }}
        >
          {/* Gold top accent bar */}
          <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #B8860B, #FFD700, #B8860B)' }} />

          {/* Corner decorations */}
          <div
            className="absolute top-6 left-6 w-16 h-16 rounded-full opacity-10 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #FFD700, transparent)' }}
          />
          <div
            className="absolute bottom-10 right-6 w-20 h-20 rounded-full opacity-10 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #FFD700, transparent)' }}
          />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition-all"
            aria-label="Close certificate"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="px-8 pt-8 pb-6">
            {/* FAM Logo */}
            <div className="flex items-center gap-2 mb-8">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #FFD700, #B8860B)' }}
              >
                <Car className="w-5 h-5 text-gray-900" />
              </div>
              <div>
                <span className="text-white font-bold text-lg tracking-widest">FAM</span>
                <p className="text-yellow-400/70 text-xs tracking-widest uppercase">Ownership Certificate</p>
              </div>
            </div>

            {/* Certificate title */}
            <div className="text-center mb-8">
              <Award
                className="w-12 h-12 mx-auto mb-3"
                style={{ color: '#FFD700' }}
              />
              <h2 className="text-white/60 text-sm tracking-widest uppercase mb-1">This certifies that</h2>
              <h1
                className="text-3xl font-bold tracking-tight"
                style={{
                  background: 'linear-gradient(90deg, #FFD700, #FFF5CC)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {data.investorName}
              </h1>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, #FFD70066)' }} />
              <span className="text-yellow-500/50 text-xs tracking-widest">IS A FRACTIONAL OWNER OF</span>
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, #FFD70066, transparent)' }} />
            </div>

            {/* Vehicle */}
            <div className="text-center mb-8">
              <p className="text-white/50 text-sm mb-1 uppercase tracking-widest">Vehicle</p>
              <p className="text-white text-2xl font-bold">
                {data.carYear} {data.carMake} {data.carModel}
              </p>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div
                className="rounded-xl p-4 text-center"
                style={{ background: 'rgba(255,215,0,0.07)', border: '1px solid rgba(255,215,0,0.2)' }}
              >
                <Percent className="w-4 h-4 mx-auto mb-1" style={{ color: '#FFD700' }} />
                <p className="text-white text-xl font-bold">{data.ownershipPct}%</p>
                <p className="text-white/50 text-xs mt-0.5 uppercase tracking-wider">Equity Share</p>
              </div>
              <div
                className="rounded-xl p-4 text-center"
                style={{ background: 'rgba(255,215,0,0.07)', border: '1px solid rgba(255,215,0,0.2)' }}
              >
                <span className="block text-yellow-500 text-lg mb-0.5">$</span>
                <p className="text-white text-xl font-bold">
                  {data.investmentAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </p>
                <p className="text-white/50 text-xs mt-0.5 uppercase tracking-wider">Invested</p>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-center justify-center gap-2 mb-8">
              <Calendar className="w-3.5 h-3.5 text-white/30" />
              <p className="text-white/40 text-xs tracking-widest">Issued {formattedDate}</p>
            </div>

            {/* Gold divider line */}
            <div className="h-px w-full mb-6" style={{ background: 'linear-gradient(90deg, transparent, #FFD70055, transparent)' }} />

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleShare}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-gray-900 transition-all hover:brightness-110 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #FFD700, #B8860B)' }}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    Share Certificate
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="px-5 py-3 rounded-xl font-semibold text-white/60 border border-white/10 hover:border-white/20 hover:text-white/80 transition-all"
              >
                Close
              </button>
            </div>
          </div>

          {/* Gold bottom accent bar */}
          <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #B8860B, #FFD700, #B8860B)' }} />
        </div>
      </div>
    </div>
  );
}
